import { Injectable, ElementRef } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { MediaServiceRegistry } from '../media-service-registry';
import { PlaybackApi, ApiPlaybackSession, ApiVOD, ApiPodcast, ApiLiveStream, ApiProduction, ApiAvAsset } from '@tytapp/api';
import { UserService } from 'user';
import { sleep } from '@tytapp/common';

/**
 * Represents a piece of media which is uniquely identified by URL.
 */
export class Media {
    constructor(url: string) {
        this._url = url;
    }

    private _session: PlaybackSession;
    private _url: string;
    private _length: number;
    private _position: number;

    /**
     * A pre-fetched media resolution, if there is one.
     */
    public resolution: AssetResolution;

    get session() {
        return this._session;
    }

    set session(value: PlaybackSession) {
        this._session = value;
    }

    get url() {
        return this._url;
    }
}

export interface PlaybackItem {
    type: "video" | "audio" | "stream";
    production?: ApiProduction;
    item?: ApiVOD | ApiPodcast | ApiLiveStream;
    playlistID?: PlaylistType;
}

/**
 * Represents a service that can play back video URLs.
 */
export interface PlaybackService {
    /**
     * When set, determines the order we attempt to resolve media using
     * registered playback services. Larger number means we try the service
     * sooner, smaller number means we try the service later.
     * If not specified, the priority is considered zero (0).
     *
     * Use this for context-sensitive playback services like GoogleCast or AirPlay.
     */
    priority?: number;

    /**
     * Returns a media resolution if this playback service
     * recognizes the given Media. If a media resolution is
     * returned, it is assumed that this playback service can
     * handle playing back the media
     */
    resolve(mediaMetadata?: PlaybackItem): AssetResolution;
    play(mediaResolution: AssetResolution, rootElement: ElementRef, hints: PlaybackHints): Promise<PlaybackSession>;

    readonly id: string;
}

export interface PlaybackHints {
    isLive?: boolean;
    startPosition?: number;
    autoplay?: boolean;
}

/**
 * Represents a playback session which has been created against
 * a piece of Media and a root ElementRef
 */
export interface PlaybackSession {

    readonly playState: PlayState;

    readonly videoDisabled?: boolean;

    readonly enableSeekOnStart?: boolean;

    /**
     * If true (or unspecified), the session
     * will receive a pause signal before being
     * dismissed.
     */
    readonly pauseOnDismiss?: boolean;

    /**
     * If true (or unspecified), the session will
     * receive a pause signal before another player
     * takes over playback.
     */
    readonly pauseOnTakeOver?: boolean;

    /**
     * If true (or unspecified), the session will
     * receive a pause signal before the session is
     * destroyed.
     */
    readonly pauseOnDestroy?: boolean;

    readonly supportedPlaybackSpeeds?: number[];

    readonly item: PlaybackItem;

    /**
     * The service which created this session.
     */
    readonly service: PlaybackService;

    /**
     * The element where the media is being played
     */
    readonly element: ElementRef;

    /**
     * The media which is being played.
     */
    readonly asset: ApiAvAsset;

    /**
     * The playback position of the media, in seconds
     */
    readonly position: number;

    readonly speedChanged?: Observable<number>;
    readonly speed?: number;
    setSpeed?(speed: number);

    /**
     * Be notified when the position changes
     */
    readonly positionChanged: Observable<number>;

    /**
     * The length of the media, in seconds
     */
    readonly length: number;

    /**
     * Be notified when the length changes
     */
    readonly lengthChanged: Observable<number>;

    /**
     * Fired when the media has finished playing.
     */
    readonly finished: Observable<void>;

    /**
     * Fired when the playback state has changed (ie paused, playing)
     */
    readonly playStateChanged: Observable<PlayState>;

    /**
     * Fired when the volume has been changed.
     */
    readonly volumeChanged: Observable<number>;

    /**
     * Fired when the player has been explicitly seek'ed.
     * Value is the new playback position in seconds.
     */
    readonly onSeek: Observable<number>;

    /**
     * True if this video has a platform-specific chat stream associated with it
     */
    readonly hasChat: boolean;

    /**
     * An HTML chunk which can be embedded to show the platform-specific chat interface
     */
    readonly chatEmbed: string;

    /**
     * Whether this media is seekable or not. If undefined, true is assumed.
     */
    readonly seekable?: boolean;

    readonly popOutURL?: string;

    /**
     * Get the current volume of the player (from 0 to 1)
     */
    readonly volume: number;

    /**
     * Available on any playback session which is based on HTML5 media playback
     */
    readonly mediaElement?: HTMLMediaElement;

    /**
     * Get current state of Closed Captions
     */
    readonly closedCaptionsEnabled?: Observable<boolean>;

    /**
     * Pause playback
     */
    pause(): Promise<void>;

    /**
     * Resume playback
     */
    resume(): Promise<void>;

    /**
     * Set current volume of the media (from 0 to 1)
     */
    setVolume(level: number);

    /**
     * Seek to a position (in seconds)
     */
    seek(position: number);

    /**
     * Called when the media player is done with this session.
     * Used to clean up any resources associated with the play session.
     */
    destroy(): Promise<void>;

    /**
     * Show or hide Closed Captions
     */
    showClosedCaptions?(value: boolean);
}

export class HTMLMediaPlaybackSession implements PlaybackSession {
    constructor(
        private _item: PlaybackItem,
        private _service: PlaybackService,
        private _element: HTMLMediaElement,
        private _asset: any,
        private _startPosition: number = 0
    ) {
        if (_startPosition >= 0) {
            if (this._element.duration > 0) {
                this._element.currentTime = _startPosition;
            } else {
                let listener = () => {
                    this._element.currentTime = _startPosition;
                    this._element.removeEventListener('loadedmetadata', listener, false);
                };

                this._element.addEventListener('loadedmetadata', listener, false);
            }
        }

        let _currentPlayState: PlayState = 'unstarted';
        let updatePositions = () => {
            this._position = _element.currentTime;
            this._positionChanged.next(this._position);

            let previousDuration = this._length;
            let newDuration = this._element.duration;

            if (previousDuration != newDuration) {
                this._length = newDuration;
                this._lengthChanged.next(this._length);
            }
        };

        let updateState = () => {
            let _newPlayState: PlayState = 'unstarted';

            this._element;
            if (this._element.paused) {
                _newPlayState = 'paused';
            } else if (this._position > 0) {
                _newPlayState = 'playing';
            }

            if (_newPlayState != _currentPlayState) {
                _currentPlayState = _newPlayState;
                this._playState = _currentPlayState;
                this._playStateChanged.next(_currentPlayState);
            }

            // TODO: reconcile new vs old play state
        };


        //this._interval = setInterval(() => updateState(), 1000);

        this._element.addEventListener('playing', () => {
            console.log(`HTML/media: event=playing`);
            if (_currentPlayState != 'playing') {
                _currentPlayState = 'playing';
                this._playState = _currentPlayState;
                this._playStateChanged.next(_currentPlayState);
            }
        });

        this._element.addEventListener('pause', () => {
            console.log(`HTML/media: event=pause`);
            if (_currentPlayState != 'paused') {
                _currentPlayState = 'paused';
                this._playState = _currentPlayState;
                this._playStateChanged.next(_currentPlayState);
            }
        });

        this._element.addEventListener('timeupdate', () => updatePositions());

        this._element.addEventListener('loadedmetadata', () => {
            console.log(`HTML/media: event=loadedmetadata`);
            updatePositions();
            this.loadClosedCaptions();
        });

        this._element.addEventListener('waiting', () => {
            console.log(`HTML/media: event=waiting`);
            if (_currentPlayState != 'buffering') {
                _currentPlayState = 'buffering';
                this._playState = _currentPlayState;
                this._playStateChanged.next(_currentPlayState);
            }
        });

        this._element.addEventListener('ended', () => {
            console.log(`HTML/media: event=ended`);
            console.info("HTML5 media playback has ended.");
            this._finished.next();
        });

        this._element.addEventListener('error', ev => {
            console.log(`HTML/media: event=error`);
            console.error("Received error while playing back HTML5 media:");
            console.dir(ev);
        });
    }

    supportedPlaybackSpeeds = [0.25, 0.5, 1, 1.25, 1.75, 1.5, 2];

    _speedChanged: Subject<number> = new BehaviorSubject<number>(1);

    get speedChanged() {
        return this._speedChanged;
    }

    _speed: number = 1;

    get speed() {
        return this._speed;
    }

    setSpeed(value: number) {
        this.element.nativeElement.playbackRate = value;
        this._speed = value;
        this._speedChanged.next(this._speed);
    }

    get item() {
        return this._item;
    }

    get playStateChanged(): Observable<PlayState> {
        return this._playStateChanged;
    }

    get volume(): number {
        return this.element.nativeElement.volume;
    }

    get mediaElement() {
        return this.element.nativeElement;
    }

    get playState() {
        return this._playState;
    }

    _playState: PlayState;

    private _volumeChanged: Subject<number> = new Subject<number>();
    get volumeChanged(): Observable<number> {
        return this._volumeChanged;
    }

    private _playStateChanged: Subject<PlayState> = new Subject<PlayState>();
    private _interval = null;
    private _position: number = null;
    private _length: number = null;
    private _positionChanged: BehaviorSubject<number> = new BehaviorSubject(null);
    private _lengthChanged: BehaviorSubject<number> = new BehaviorSubject(null);
    private _finished: Subject<void> = new Subject<void>();
    private _onSeek: Subject<number> = new Subject<number>();
    private _closedCaptionsEnabled: Subject<boolean> = new Subject<boolean>();

    get onSeek(): Observable<number> {
        return this._onSeek;
    }

    get service(): PlaybackService {
        return this._service;
    }

    get finished(): Observable<void> {
        return this._finished;
    }

    get length() {
        return this._length;
    }

    get lengthChanged(): Observable<number> {
        return this._lengthChanged;
    }

    get positionChanged(): Observable<number> {
        return this._positionChanged;
    }

    get position() {
        return this._position;
    }

    get element() {
        return { nativeElement: this._element };
    }

    get closedCaptionsEnabled(): Observable<boolean> {
        return this._closedCaptionsEnabled;
    }

    readonly seekable = true;

    seek(positionInSeconds: number) {
        this._element.currentTime = positionInSeconds;
        this._onSeek.next(positionInSeconds);
    }

    protected removeElementOnDestroy: boolean = true;

    async destroy() {
        clearInterval(this._interval);
        this._interval = null;

        this._element.src = '';

        if (this.removeElementOnDestroy)
            this._element.remove();
        await sleep(0.5);
    }

    async pause() {
        await this._element.pause();
    }

    get asset() {
        return this._asset;
    }

    readonly hasChat = false;
    readonly chatEmbed = null;

    async resume() {
        await this._element.play();
    }

    setVolume(volumeFactor: number) {
        this._element.volume = volumeFactor;
        this._volumeChanged.next(volumeFactor);
    }

    loadClosedCaptions(): void {
        const track = document.createElement('track');
        track.kind = 'captions';
        track.label = 'video captions';
        this._element.crossOrigin = 'anonymous';
        //this._asset.vtt_url = 'https://cdn.jwplayer.com/tracks/IPHwZY3K'; // for local tests
        track.src = this._asset.vtt_url;
        this._element.appendChild(track);
        this._closedCaptionsEnabled.next(false);
    }

    showClosedCaptions(show: boolean) {
        this._element.textTracks[0].mode = show ? 'showing' : 'hidden';
        this._closedCaptionsEnabled.next(show);
    }


}

export type PlaylistType = 'episodes' | 'podcasts' | 'clips' | null;
export type PlayState =
    'unstarted'
    | 'playing'
    | 'paused'
    | 'buffering'
    ;

export interface Player {
    revokePlayback();

    readonly currentSessionChanged: Observable<PlaybackSession>;
    readonly playlistEnded: Observable<void>;

    /**
     * Tells the player to re-resolve the current media. Use this when the
     * playback plugin that would be selected is expected to change.
     */
    refreshMediaResolution(autoplay: boolean): Promise<PlaybackSession>;
    playAsset(
        production: ApiProduction,
        playlistID: PlaylistType,
        playlist: (ApiPodcast | ApiVOD | ApiLiveStream)[],
        media: ApiPodcast | ApiVOD | ApiLiveStream,
        asset: ApiAvAsset,
        assetType: "audio" | "video" | "stream",
        position?: number);
    nextItem();
    previousItem();
    getCurrentPlaybackSession(): PlaybackSession;
}

interface PendingSessionLookup {
    promise: Promise<ApiPlaybackSession>;
    resolve: Function;
    reject: Function;
    id: string;
    started: boolean;
}

@Injectable()
export class Playback {
    constructor(
        private serviceRegistry: MediaServiceRegistry,
        private playbackApi: PlaybackApi,
        private userService: UserService
    ) {

    }

    get services(): PlaybackService[] {
        return this.serviceRegistry.all;
    }

    private pendingSessionLookups = {};
    private pendingSessionLookupTimeout;

    async getSessionFor(item: ApiPodcast | ApiVOD) {
        return await this.getSessionForId(item.id);
    }

    async getSessionForId(id: string) {
        if (this.pendingSessionLookups[id])
            return await this.pendingSessionLookups[id].promise;

        if (!this.userService.user || !this.userService.user.entitled)
            return null;

        if (typeof window === 'undefined')
            return null;

        let resolve, reject;
        let promise = new Promise<ApiPlaybackSession>((res, rej) => (resolve = res, reject = rej));

        let pendingLookup: PendingSessionLookup = {
            id,
            promise,
            resolve,
            reject,
            started: false
        };

        this.pendingSessionLookups[id] = pendingLookup;

        this.fetchPendingLookups();
        return await pendingLookup.promise;
    }

    fetchPendingLookups() {
        clearTimeout(this.pendingSessionLookupTimeout);

        this.pendingSessionLookupTimeout = setTimeout(async () => {
            let itemsToStart: PendingSessionLookup[] = Object.keys(this.pendingSessionLookups)
                .map(x => this.pendingSessionLookups[x])
                .filter(x => !x.started)
                .slice(0, 99)
                ;

            if (itemsToStart.length == 0)
                return;

            // You must be logged in and entitled for playhead tracking.

            if (!this.userService.user || !this.userService.user.entitled) {
                itemsToStart.forEach(x => x.resolve(null));
                return;
            }

            // Mark them as started...
            itemsToStart.forEach(x => x.started = true);

            // Query for the requested status(es)
            let results: ApiPlaybackSession[];

            try {
                results = <any>await this.playbackApi.getSessions({ items: itemsToStart.map(x => x.id) }).toPromise();
            } catch (e) {
                itemsToStart.forEach(x => x.reject(e));
                return;
            }

            itemsToStart.forEach(item => {
                delete this.pendingSessionLookups[item.id];
                item.resolve(results.find(x => x.playlist_item_id === item.id));
            });

            // If we have more to look up, do another round

            if (Object.keys(this.pendingSessionLookups).length > 0)
                this.fetchPendingLookups();
        }, 1000);
    }

    async getSessions(productions: ApiProduction[], playlist: string = 'all'): Promise<ApiPlaybackSession[]> {

        if (!productions) {
            console.error(`Playback.getSessions(): Caller passed null for productions array`);
            return [];
        }

        if (!playlist) {
            console.error(`Playback.getSessions(): Caller passed null for playlist`);
            return [];
        }

        let playlistItemIds: string[] = [];

        for (let production of productions) {
            if (playlist === 'all' || playlist === 'episodes')
                for (let video of production.full_length_vods)
                    playlistItemIds.push(video.id);

            if (playlist === 'all' || playlist === 'clips')
                for (let video of production.vod_clips)
                    playlistItemIds.push(video.id);

            if (playlist === 'all' || playlist === 'podcasts')
                for (let video of production.full_length_podcasts)
                    playlistItemIds.push(video.id);
        }

        let sessionsResult: ApiPlaybackSession[];

        try {
            sessionsResult = <any>await this.playbackApi.getSessions({ items: playlistItemIds }).toPromise();
        } catch (e) {
            console.error(`Caught error while trying to fetch playback session status for ${playlistItemIds.length} items`);
        }

        return sessionsResult;
    }

    private _playerStarted: Subject<Player> = new Subject<Player>();

    /**
     * Set to true if you need all player controls to remain visible.
     * This is used by remote play solutions like Google Cast and Airplay.
     */
    lockControlsVisible: boolean = false;

    /**
     * Set this to true to induce a constant buffering state. Useful for
     * orchestrated transitions like those found in Chromecast.
     */
    globalBuffering: boolean = false;

    /**
     * Whether or not playback session tracking (updates) are enabled.
     * This does not disable playback session start, only sending
     * updates to the server. This setting should be set to false
     * if in a remote playback scenario where the remote is handling
     * updating the play session (such as Chromecast).
     */
    enableSessionTracking: boolean = true;

    get playerStarted(): Observable<Player> {
        return this._playerStarted;
    }

    private _currentPlayer: Player = null;
    takePlayback(player: Player) {
        if (this._currentPlayer == player)
            return true; // have at it, hass

        if (this._currentPlayer)
            this._currentPlayer.revokePlayback();

        this._currentPlayer = player;
        this._playerStarted.next(this._currentPlayer);

        return true;
    }

    public get currentPlayer() {
        return this._currentPlayer;
    }

    revokePlayback() {
        if (this._currentPlayer)
            this._currentPlayer.revokePlayback();
        this._currentPlayer = null;
    }

    private _session: PlaybackSession;
    private _sessionChanged: BehaviorSubject<PlaybackSession> = new BehaviorSubject<PlaybackSession>(null);
    registerSession(session: PlaybackSession) {
        this._session = session;
        this._sessionChanged.next(this._session);
    }

    public get sessionChanged(): Observable<PlaybackSession> {
        return this._sessionChanged;
    }

    public get appPlayer() {
        return this._appPlayer;
    }

    public get isAppPlayerActive() {
        return this._currentPlayer === this._appPlayer;
    }

    private _appPlayer: Player = null;
    registerAppPlayer(appPlayer: Player) {
        this._appPlayer = appPlayer;
    }

    transferPlaybackToApp(
        production: ApiProduction,
        playlistID: PlaylistType,
        playlist: (ApiVOD | ApiPodcast | ApiLiveStream)[],
        item: ApiVOD | ApiPodcast | ApiLiveStream,
        assetType: "audio" | "video" | "stream",
        position: number
    ) {
        if (!this._appPlayer)
            return false;

        this._appPlayer.playAsset(production, playlistID, playlist, item, item.asset, assetType, position);
        return true;
    }

    /**
     * Find a service which can play back the given media
     */
    resolve(playbackItem: PlaybackItem, useCachedResolution: boolean = true): AssetResolution {
        let asset: ApiAvAsset = playbackItem.item.asset;

        if (asset == null) {
            debugger;
            throw new Error("Cannot resolve null media");
        }

        if (useCachedResolution && asset['resolution'])
            return asset['resolution'];

        let resolution: AssetResolution = null;
        let orderedServices = this.services.slice(0).sort((a, b) => ((b.priority || 0) - (a.priority || 0)));

        orderedServices.find(x => null != (resolution = x.resolve(playbackItem)));

        if (!resolution) {
            console.error(`Playback: Error: Could not locate service capable of playing:`);
            console.dir(playbackItem);
            return null;
        } else {
            console.log(`Playback: Selected service ${resolution.service.id} for playback`);
        }

        resolution.url = asset.url;

        return resolution;
    }
}

export interface AssetResolution {
    url?: string;
    readonly item: PlaybackItem;
    readonly asset: ApiAvAsset;
    readonly service: PlaybackService;
    readonly data: any;
}
